/**
 * VH-83: Visual DAG of recipe versions.
 *
 * For VH-83 we render the DAG as a flat, indented list grouped by branch
 * label — each version is a row with parent indication, branch label badge,
 * timestamp, and "Make active" / "View" actions. Native lists are simpler to
 * implement and a11y-friendly than a free-form SVG; future iterations may
 * upgrade to a real graph layout.
 */

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CheckCircleIcon,
  ArrowUturnUpIcon,
  EyeIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'

function formatDate(iso, lang) {
  if (!iso) return ''
  try {
    const locale = lang === 'de' ? 'de-DE' : 'en-US'
    return new Date(iso).toLocaleString(locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

export default function VersionDAGViewer({
  versions = [],
  currentVersionId,
  onSwitch,
  onView,
  onDelete,
  switching = false,
  deleting = false,
}) {
  const { t, i18n } = useTranslation()

  // Sort: reverse-chronological so the newest version is on top (descending).
  // Ties on created_at fall back to id so ordering is stable.
  const sorted = useMemo(
    () =>
      [...versions].sort((a, b) => {
        const diff =
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        return diff !== 0 ? diff : b.id - a.id
      }),
    [versions]
  )

  if (sorted.length === 0) {
    return (
      <p className="text-sm text-gray-500">{t('recipes.versions.noVersions')}</p>
    )
  }

  return (
    <ul className="space-y-2" data-testid="version-dag">
      {sorted.map((v) => {
        const isCurrent = v.id === currentVersionId
        return (
          <li
            key={v.id}
            className={`rounded-md border p-3 ${
              isCurrent
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 bg-surface-container'
            }`}
            data-testid={`version-row-${v.id}`}
          >
            {/* Info block, then actions on their own wrapping row below.
                Stacking (rather than a side-by-side flex) prevents the wide
                "Make this the active version" button from overlapping the
                title in the narrow version sidebar. */}
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-gray-900">
                  {v.title}
                </span>
                {isCurrent && (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 bg-primary-100 px-2 py-0.5 rounded">
                    <CheckCircleIcon className="h-3 w-3" />
                    {t('recipes.detail.currentVersion')}
                  </span>
                )}
                {v.branch_label && (
                  <span className="text-xs font-medium text-purple-700 bg-purple-100 px-2 py-0.5 rounded">
                    {t('recipes.detail.branchLabel')}: {v.branch_label}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                {formatDate(v.created_at, i18n.language)}
                {v.parent_version_id && (
                  <span className="ml-2 text-gray-400">
                    ← #{v.parent_version_id}
                  </span>
                )}
              </p>
              {v.change_reason && (
                <p className="text-xs text-gray-600 mt-1 italic">
                  {v.change_reason}
                </p>
              )}
            </div>
            {(onView || (!isCurrent && (onSwitch || onDelete))) && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {onView && (
                  <button
                    type="button"
                    onClick={() => onView(v)}
                    className="btn-secondary text-xs flex items-center gap-1"
                    title={t('common.edit', 'View')}
                  >
                    <EyeIcon className="h-3 w-3" />
                  </button>
                )}
                {!isCurrent && onSwitch && (
                  <button
                    type="button"
                    onClick={() => onSwitch(v)}
                    disabled={switching}
                    className="btn-secondary text-xs flex items-center gap-1"
                  >
                    <ArrowUturnUpIcon className="h-3 w-3" />
                    {t('recipes.actions.switchToThisVersion')}
                  </button>
                )}
                {/* The active version can't be deleted (switch first) —
                    matches the backend guard, so no button is shown. */}
                {!isCurrent && onDelete && (
                  <button
                    type="button"
                    onClick={() => onDelete(v)}
                    disabled={deleting}
                    className="btn-secondary text-xs flex items-center gap-1 text-red-600 hover:bg-red-50"
                    title={t('recipes.actions.deleteVersion')}
                    data-testid={`delete-version-${v.id}`}
                  >
                    <TrashIcon className="h-3 w-3" />
                  </button>
                )}
              </div>
            )}
          </li>
        )
      })}
    </ul>
  )
}
